import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import { useAppDispatch } from "../../Redux/Store/Hooks";
import { ITemplate } from "./IPolicyTemplate";
import { PolicyCreate_Update } from "../../Redux/Slices/PrivacyPolicy/policyMasterCreate_Update";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./PolicyTemplate.scss";

interface TemplateFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<ITemplate>;
  setInitialValues: React.Dispatch<React.SetStateAction<Partial<ITemplate>>>;
}

const PolicyTemplateForm: FunctionComponent<TemplateFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
}) => {
  const dispatch = useAppDispatch();

  // ✅ Quill setup (fonts, sizes, align, etc.)
  const FontSize = ReactQuill.Quill.import("formats/size");
  FontSize.whitelist = ["small", false, "large", "huge"];
  ReactQuill.Quill.register(FontSize, true);

  const Align = ReactQuill.Quill.import("formats/align");
  Quill.register(Align, true);

  const Font = Quill.import("formats/font");
  Font.whitelist = ["system", "roboto", "times-new-roman", "courier-new", "monospace"];
  Quill.register(Font, true);

  const modules = {
    toolbar: [
      [
        { header: [false, 1, 2, 3, 4, 5, 6] },
        {
          font: ["system", "roboto", "times-new-roman", "courier-new", "monospace"],
        },
      ],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ direction: "rtl" }],
      ["blockquote", "code-block"],
      ["image", "video", "formula"],
      ["clean"],
    ],
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
  };

  const formats = [
    "video",
    "header",
    "font",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "list",
    "bullet",
    "indent",
    "direction",
    "blockquote",
    "code-block",
    "align",
    "image",
    "formula",
  ];

  // ✅ Zod schema
  const templateSchema = z.object({
    template_id: z.string().default("0"),
    template_name: z
      .string({ required_error: "Please enter template name" })
      .min(1, { message: "Template name is required" }),
    template_content: z
      .string({ required_error: "Please enter content" })
      .min(1, { message: "Content is required" }),
    status: z.boolean({ required_error: "Please select status" }),
    is_delete: z.boolean().optional().default(false),
  });

  // ✅ Hook Form setup
  const methods = useForm<ITemplate>({
    defaultValues: {
      template_id: initialValues?.template_id ?? "0",
      template_name: "",
      template_content: "",
      status: true,
      is_delete: false,
      ...initialValues,
    },
    resolver: zodResolver(templateSchema),
    mode: "onChange",
  });

  // Reset form when initialValues change
  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues, methods]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      setInitialValues({});
    };
  }, [setInitialValues]);

  // ✅ Submit handler
  const onSubmit: SubmitHandler<ITemplate> = (values) => {
    const payload = {
      ...values,
      template_id: values.template_id ?? "0",
    };
    dispatch(PolicyCreate_Update(payload));
  };

  // ✅ Dropdown options
  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  return (
    <FormProvider {...methods}>
      <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="form_double_input">
          {/* Template Name */}
          <TextInputFieldC name="template_name" label="Template Name" type="text" />

          {/* Status Dropdown */}
          <Select
            name="status"
            label="Status"
            options={statusOptions}
            value={
              statusOptions.find(
                (option) => option.value === methods.getValues("status")
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              methods.setValue("status", val?.value, { shouldValidate: true });
            }}
          />
        </div>

        {/* Rich Content Editor */}
        <Controller
          name="template_content"
          control={methods.control}
          render={({ field }) => (
            <ReactQuill
              className="custom-quill2"
              style={{ width: "100%", paddingTop: "20px", borderRadius: "10px" }}
              theme="snow"
              value={field.value || ""}
              onChange={(content) => field.onChange(content)}
              modules={modules}
              formats={formats}
              placeholder="Create content here..."
            />
          )}
        />
      </form>
    </FormProvider>
  );
};

export default memo(PolicyTemplateForm);
